import {
  makeAllDayTimeRange,
  makeTimeRange,
  makeTimeRecord,
  TimeRange,
  makeDateRecord,
  type DateRecord,
} from "@visioni/schedules";
import { parseISO } from "date-fns";

export type GridState = { [key: string]: boolean }; // key format: "DAY-HOUR" or "DAYINDEX-HOUR" or "DAY-SLOT" for granularity support

/**
 * Type guard to check if a range is an all-day range
 */
export function isAllDayRange(range: unknown): boolean {
  return typeof range === "object" && range !== null && "kind" in range && range.kind === "all_day";
}

/**
 * Type guard to check if a range is a time range
 */
export function isTimeRange(range: unknown): range is TimeRange {
  return typeof range === "object" && range !== null && "start" in range && "end" in range;
}

/**
 * Marks all 24 hours for a given day in the grid state
 * @param gridState - The grid state to update
 * @param dayKey - The day identifier (day name like "MON" or day index like "0")
 */
export function markAllDayHours(gridState: GridState, dayKey: string | number): void {
  for (let hour = 0; hour < 24; hour++) {
    gridState[`${dayKey}-${hour}`] = true;
  }
}

/**
 * Marks hours in a time range for a given day in the grid state
 * @param gridState - The grid state to update
 * @param dayKey - The day identifier (day name like "MON" or day index like "0")
 * @param timeRange - The time range to mark
 */
export function markTimeRangeHours(
  gridState: GridState,
  dayKey: string | number,
  timeRange: { start: { hour: number; minute: number }; end: { hour: number; minute: number } },
): void {
  const startHour = timeRange.start.hour;
  let endHour = timeRange.end.hour;

  // Handle overnight ranges (end hour 0 means midnight of next day)
  if (endHour === 0 && timeRange.end.minute === 0) {
    endHour = 24;
  }

  // Handle overnight spans (e.g., 18:00 to 06:00)
  if (startHour > endHour || (startHour === endHour && endHour === 24)) {
    // Overnight range: mark from start to 24, then from 0 to end
    for (let hour = startHour; hour < 24; hour++) {
      gridState[`${dayKey}-${hour}`] = true;
    }
    for (let hour = 0; hour < (endHour === 24 ? 0 : endHour); hour++) {
      gridState[`${dayKey}-${hour}`] = true;
    }
  } else {
    // Normal range
    for (let hour = startHour; hour < endHour; hour++) {
      gridState[`${dayKey}-${hour}`] = true;
    }
  }
}

/**
 * Converts an array of hours into consecutive time ranges
 * When all 24 hours are selected (ending with hour 23), the end time is set to 24:00 instead of 00:00
 * to represent "all day armed" correctly.
 * @param hours - Array of hour numbers (0-23)
 * @returns Array of TimeRange objects
 */
export function convertHoursToTimeRanges(hours: number[]): TimeRange[] {
  if (hours.length === 0) return [];

  // Sort hours to ensure consecutive grouping works correctly
  const sortedHours = [...hours].sort((a, b) => a - b);
  const ranges: Array<{ start: number; end: number }> = [];
  let start = sortedHours[0];
  let prev = sortedHours[0];

  for (let i = 1; i <= sortedHours.length; i++) {
    const current = sortedHours[i];
    if (current !== prev + 1 || i === sortedHours.length) {
      // When the last hour is 23, use 24:00 instead of 00:00 to represent end of day
      const end = prev === 23 ? 24 : prev + 1;
      ranges.push({ start, end });
      if (i < sortedHours.length) {
        start = current;
      }
    }
    prev = current;
  }

  return ranges.map(({ start, end }) => makeTimeRange(makeTimeRecord(start, 0), makeTimeRecord(end, 0)));
}

/**
 * Converts a DateRecord to an ISO string
 * Creates a date at noon UTC to avoid timezone shifts
 */
export function dateRecordToISOString(dateRecord: DateRecord): string {
  const date = new Date(Date.UTC(dateRecord.year, dateRecord.month - 1, dateRecord.day, 12, 0, 0));
  return date.toISOString();
}

/**
 * Converts an ISO string to a DateRecord
 */
export function isoStringToDateRecord(isoString: string): DateRecord {
  const datePart = isoString.split("T")[0]; // Get "YYYY-MM-DD"
  const [year, month, day] = datePart.split("-").map(Number);
  return makeDateRecord(year, month, day);
}

/**
 * Formats an ISO date string using the user's locale
 */
export function formatDate(dateString: string): string {
  const date = parseISO(dateString);
  const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  return new Intl.DateTimeFormat(userLocale).format(date);
}

/**
 * Marks all slots for a given day in the grid state (for granularity support)
 * @param gridState - The grid state to update
 * @param dayKey - The day identifier (day name like "MON" or day index like "0")
 * @param slotsPerDay - Number of slots per day based on granularity
 */
export function markAllDaySlots(gridState: GridState, dayKey: string | number, slotsPerDay: number): void {
  for (let slot = 0; slot < slotsPerDay; slot++) {
    gridState[`${dayKey}-${slot}`] = true;
  }
}

/**
 * Marks slots in a time range for a given day in the grid state (for granularity support)
 * @param gridState - The grid state to update
 * @param dayKey - The day identifier (day name like "MON" or day index like "0")
 * @param timeRange - The time range to mark
 * @param granularity - The granularity in minutes (15, 30, or 60)
 */
export function markTimeRangeSlots(
  gridState: GridState,
  dayKey: string | number,
  timeRange: { start: { hour: number; minute: number }; end: { hour: number; minute: number } },
  granularity: number,
): void {
  const startMinutes = timeRange.start.hour * 60 + timeRange.start.minute;
  const endHour = timeRange.end.hour === 24 ? 24 : timeRange.end.hour;
  let endMinutes = endHour * 60 + timeRange.end.minute;

  // Handle overnight ranges
  if (endMinutes <= startMinutes && endHour !== 24) {
    endMinutes += 24 * 60;
  }

  const startSlot = Math.floor(startMinutes / granularity);
  const endSlot = Math.ceil(endMinutes / granularity);
  const slotsPerDay = (24 * 60) / granularity;

  for (let slot = startSlot; slot < endSlot; slot++) {
    gridState[`${dayKey}-${slot % slotsPerDay}`] = true;
  }
}

/**
 * Converts an array of slots into consecutive time ranges (for granularity support)
 * @param slots - Array of slot numbers
 * @param granularity - The granularity in minutes (15, 30, or 60)
 * @returns Array of TimeRange objects
 */
export function convertSlotsToTimeRanges(slots: number[], granularity: number): TimeRange[] {
  if (slots.length === 0) return [];

  const slotsPerDay = (24 * 60) / granularity;
  const sortedSlots = [...slots].sort((a, b) => a - b);
  const ranges: Array<{ start: { hour: number; minute: number }; end: { hour: number; minute: number } }> = [];
  let start = sortedSlots[0];
  let prev = sortedSlots[0];

  for (let i = 1; i <= sortedSlots.length; i++) {
    const current = sortedSlots[i];
    if (current !== prev + 1 || i === sortedSlots.length) {
      const startTime = {
        hour: Math.floor((start * granularity) / 60),
        minute: (start * granularity) % 60,
      };
      const endSlot = prev + 1;
      const endMinutes = endSlot * granularity;
      let endTime = {
        hour: Math.floor(endMinutes / 60),
        minute: endMinutes % 60,
      };

      // Handle end of day
      if (endTime.hour >= 24) {
        endTime = { hour: 24, minute: 0 };
      }

      ranges.push({ start: startTime, end: endTime });
      if (i < sortedSlots.length) {
        start = current;
      }
    }
    prev = current;
  }

  return ranges.map(({ start, end }) =>
    makeTimeRange(makeTimeRecord(start.hour, start.minute), makeTimeRecord(end.hour, end.minute)),
  );
}
